import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button, CircularProgress } from '@mui/material';
import { BugReport as TraceIcon, Refresh as RefreshIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import api from '../services/api';

export interface SipMsg {
  t: number; dir: string; proto: string; src: string; dst: string;
  src_host: string; dst_host: string; kind: 'req' | 'resp'; code: number;
  label: string; cseq: string; raw: string;
}
export interface SipDiagnosis {
  answered: boolean; final_code: number; label: string; nature: string;
  codes_seen: number[]; summary: string;
}
export interface SipTrace {
  call_id: string;
  count?: number;
  hosts?: string[];
  messages?: SipMsg[];
  diagnosis?: SipDiagnosis;
  trace: string;
  captured_at: string;
}

export const SipLadder: React.FC<{ trace: SipTrace }> = ({ trace }) => {
  const hosts = trace.hosts || [];
  const msgs = trace.messages || [];
  const N = hosts.length || 1;
  const GUT = 66; // px gutter for the timestamp column
  const colorFor = (m: SipMsg) => m.kind === 'resp'
    ? (m.code >= 400 ? '#ff5252' : m.code >= 300 ? '#ffb74d' : m.code >= 200 ? '#00e676' : '#ffd54f')
    : '#40c4ff';
  const center = (i: number) => `calc(${GUT}px + (100% - ${GUT}px) * ${(i + 0.5) / N})`;
  const span = (lo: number, hi: number) => `calc((100% - ${GUT}px) * ${(hi - lo) / N})`;
  return (
    <Box sx={{ position: 'relative', bgcolor: '#050810', maxHeight: 520, overflowY: 'auto', overflowX: 'hidden', pb: 2 }}>
      <Box sx={{ position: 'sticky', top: 0, zIndex: 2, bgcolor: '#0a0e1a', borderBottom: '1px solid #1e2a3a', height: 34 }}>
        {hosts.map((h, i) => (
          <Typography key={i} title={h} sx={{ position: 'absolute', top: 8, left: center(i), transform: 'translateX(-50%)', fontSize: 11, fontWeight: 700, color: '#b0bec5', whiteSpace: 'nowrap' }}>{h}</Typography>
        ))}
      </Box>
      <Box sx={{ position: 'relative', pt: 1 }}>
        {hosts.map((_, i) => (
          <Box key={i} sx={{ position: 'absolute', top: 0, bottom: 0, left: center(i), width: '1px', bgcolor: '#16202e' }} />
        ))}
        {msgs.map((m, idx) => {
          const si = hosts.indexOf(m.src_host), di = hosts.indexOf(m.dst_host);
          if (si < 0 || di < 0 || si === di) return null;
          const lo = Math.min(si, di), hi = Math.max(si, di);
          const right = di > si;
          const color = colorFor(m);
          const ts = new Date(m.t * 1000).toISOString().substr(11, 12);
          const cseqNum = (m.cseq || '').split(' ')[0];
          return (
            <Box key={idx} sx={{ position: 'relative', height: 42 }}>
              <Typography sx={{ position: 'absolute', left: 0, top: 16, width: GUT - 8, textAlign: 'right', fontSize: 10, color: '#546e7a', fontFamily: 'monospace' }}>{ts}</Typography>
              <Typography sx={{ position: 'absolute', top: 4, left: center(lo), width: span(lo, hi), textAlign: 'center', fontSize: 11, fontWeight: 600, color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {m.label}{cseqNum ? `  ·  ${cseqNum}` : ''}
              </Typography>
              <Box sx={{ position: 'absolute', top: 24, left: center(lo), width: span(lo, hi), height: 0, borderTop: `2px ${m.kind === 'resp' ? 'dashed' : 'solid'} ${color}` }}>
                <Box sx={{ position: 'absolute', top: -6, [right ? 'right' : 'left']: -3, color, fontSize: 11, lineHeight: 1 }}>{right ? '▶' : '◀'}</Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// Per-call "why did this call end this way" banner.
const DiagnosisBar: React.FC<{ d: SipDiagnosis }> = ({ d }) => {
  const color = d.answered ? '#00e676'
    : (d.nature === 'capacity' || d.nature === 'carrier_error' || d.nature === 'capacity_or_carrier') ? '#ff5252'
    : d.nature === 'policy' ? '#ffd54f' : '#ffb74d';
  return (
    <Box sx={{ px: 2, py: 1.2, bgcolor: '#0a0e1a', borderBottom: '1px solid #1e2a3a', borderLeft: `3px solid ${color}` }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color }}>
        {d.answered ? '✓ ' : '✕ '}{d.summary}
      </Typography>
      {d.codes_seen && d.codes_seen.length > 0 && (
        <Typography sx={{ fontSize: 11, color: '#607d8b', mt: 0.3 }}>
          Failure codes seen on the routes: {d.codes_seen.join(', ')}
        </Typography>
      )}
    </Box>
  );
};

export const SipTraceModal: React.FC<{ callId: string | null; open: boolean; onClose: () => void }> = ({ callId, open, onClose }) => {
  const [trace, setTrace] = useState<SipTrace | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [rawView, setRawView] = useState(false);

  const fetchTrace = useCallback(async () => {
    if (!callId) return;
    setLoading(true);
    setTrace(null);
    try {
      const res = await api.get<SipTrace>(`/calls/sip-trace`, { params: { call_id: callId } });
      setTrace(res.data);
    } catch {
      setTrace({ call_id: callId, trace: 'Failed to load SIP trace. The capture for this call may have rolled off (recent calls only).', captured_at: new Date().toISOString() });
    } finally {
      setLoading(false);
    }
  }, [callId]);

  useEffect(() => { if (open && callId) fetchTrace(); }, [open, callId, fetchTrace]);

  const handleCopy = () => {
    if (trace?.trace) { navigator.clipboard.writeText(trace.trace); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { bgcolor: '#0d1117', border: '1px solid #1e2a3a' } }}>
      <DialogTitle sx={{ color: '#e0e0e0', borderBottom: '1px solid #1e2a3a', pb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TraceIcon sx={{ color: '#00b0ff', fontSize: 20 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>SIP Trace</Typography>
          <Typography variant="caption" sx={{ color: '#607d8b', wordBreak: 'break-all' }}>{callId}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {trace?.diagnosis && !loading && <DiagnosisBar d={trace.diagnosis} />}
        <Box sx={{ px: 2, py: 1, bgcolor: '#0a0e1a', borderBottom: '1px solid #1e2a3a', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ color: '#607d8b', fontStyle: 'italic', flex: 1 }}>
            {trace?.count ? `${trace.count} SIP messages across ${trace.hosts?.length || 0} endpoints` : (trace && !loading ? 'No capture for this call' : 'SIP ladder from Kamailio capture')}
          </Typography>
          <Button size="small" onClick={() => setRawView(v => !v)} sx={{ color: '#90a4ae', fontSize: 11, minWidth: 0 }}>
            {rawView ? 'Ladder view' : 'Raw view'}
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={32} sx={{ color: '#00b0ff', mb: 1 }} />
              <Typography variant="caption" sx={{ color: '#607d8b', display: 'block' }}>Loading SIP trace...</Typography>
            </Box>
          </Box>
        ) : !trace ? null : (!rawView && trace.messages && trace.messages.length > 0) ? (
          <SipLadder trace={trace} />
        ) : (
          <Box component="pre" sx={{ m: 0, p: 2, fontFamily: 'monospace', fontSize: '0.75rem', color: '#00e676', bgcolor: '#050810', maxHeight: 500, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.6 }}>
            {trace.trace}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #1e2a3a', px: 2, py: 1.5, gap: 1 }}>
        <Button size="small" startIcon={<RefreshIcon />} onClick={fetchTrace} disabled={loading} sx={{ color: '#00b0ff', borderColor: '#1e2a3a', '&:hover': { borderColor: '#00b0ff' } }} variant="outlined">Re-fetch</Button>
        <Button size="small" startIcon={<CopyIcon />} onClick={handleCopy} disabled={!trace} sx={{ color: copied ? '#00e676' : '#90a4ae', borderColor: '#1e2a3a' }} variant="outlined">{copied ? 'Copied!' : 'Copy'}</Button>
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={onClose} sx={{ color: '#607d8b' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
